/**
 * Facts about Point2 that more than one page states.
 *
 * Every public contact, the company identity and every stable URL live here so
 * that the footer, the legal documents, `/support` and `/delete-account` cannot
 * disagree. Values were confirmed by the business owner on 2026-10-05; nothing
 * here may be replaced with a plausible-looking guess. Hours and a registered
 * address were not supplied, so no page states them.
 */

/** Canonical public origin. Used for metadata, canonical URLs and the sitemap. */
export const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL?.trim() || 'https://www.point2.app').replace(/\/+$/, '');

export const COMPANY = {
  legalName: 'POINT2 DIGITAL LTD',
  registrationNumber: 'RC2389271298',
  brand: 'Point2',
} as const;

export const CONTACT = {
  supportEmail: 'support@point2.app',
  /** Displayed as written; dialled in E.164. */
  supportPhoneDisplay: '0811 233 6839',
  supportPhoneTel: '+2348112336839',
} as const;

export const mailto = (subject?: string) =>
  `mailto:${CONTACT.supportEmail}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`;

export const telHref = `tel:${CONTACT.supportPhoneTel}`;

/** Stable public paths. The mobile apps link to these (see packages `LEGAL_URLS`). */
export const ROUTES = {
  home: '/',
  earn: '/earn',
  /** The public safety overview. The policy itself is `safety` below. */
  safetyOverview: '/safety',
  waitlist: '/waitlist',
  /** Emailed-link pages. Never in the sitemap; `noindex`. */
  waitlistVerify: '/waitlist/verify',
  waitlistManage: '/waitlist/manage',
  waitlistUnsubscribe: '/waitlist/unsubscribe',
  waitlistInvite: '/waitlist/invite',
  support: '/support',
  deleteAccount: '/delete-account',
  legal: '/legal',
  privacy: '/privacy',
  terms: '/terms',
  operatorTerms: '/operator-terms',
  payments: '/legal/payments',
  safety: '/legal/safety',
  copyright: '/legal/copyright',
} as const;

export const absoluteUrl = (path: string) => `${SITE_URL}${path === '/' ? '' : path}`;

/** Primary navigation, identical on every page that has the site header. */
export const PRIMARY_NAV = [
  { href: ROUTES.home, label: 'For customers' },
  { href: ROUTES.earn, label: 'Earn with Point2' },
  { href: ROUTES.safetyOverview, label: 'Safety' },
  { href: ROUTES.support, label: 'Support' },
] as const;

/** Pre-launch, the one conversion is the waitlist. Swap for store links at launch. */
export const PRIMARY_CTA = { href: ROUTES.waitlist, label: 'Join the waitlist' } as const;
